import * as React from 'react';
import { connect } from 'react-redux';
import MatchList, { MatchListAddMoreAnchor } from '../../match-list';
import { AppState } from '../../../../core/types';
import { areMatchOfTheDayMatchListTopicPropsEqual, getMatchOfTheDayMatchListSliceName } from './match-of-the-day-match-list.utils';
import {
    addMoreMatchOfTheDayMatchList,
    getMatchOfTheDayMatchGroupsOverviewList,
    removeMatchOfTheDayMatchGroupsOverviewList,
    updateMatchOfTheDayMatchGroupsOverviewList,
} from './match-of-the-day-match-list.actions';
import { MatchOfTheDayMatchListStoreModel } from './match-of-the-day-match-list.constants';
import { compose } from 'redux';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import withRouting from '@om-fe/core/core/routing/with-routing';
import { EMRoutingContextInterface } from '@om-fe/core/core/routing/utils';
import { MatchListCommon, FilteredMarketGroupsOverview } from '../../utils/match-list-models';
import { DEFAULT_DISPLAYED_NUMBER_OF_MATCHES, MatchListItemLayout } from '../../utils/match-list.constants';
import MatchListSkeleton from '../../match-list-skeleton';
import difference from 'lodash/difference';
import { PlatformTypes } from '@om-fe/utils';
import { DisciplineLiveStatus } from '@om-fe/models';

export interface MatchOfTheDayMatchListPublicProps extends MatchListCommon {
    readonly instanceId: string;
    readonly disciplineId: string;
    readonly dateRange: string;
    readonly redirectAnchor?: MatchListAddMoreAnchor;
    readonly itemLayout?: MatchListItemLayout;
    readonly groupOverview?: string;
    readonly hasMarketSelector?: boolean;
    readonly selectGroupsOverview?: (item: string) => void;
    readonly filteredMarketGroupsOverviewList?: string[];
    readonly filteredMarketGroupsOverview?: Record<string, FilteredMarketGroupsOverview>;
    readonly isHasDrawer?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly locationId?: string;
    readonly eventCategoryId?: string;
    readonly preventRedirect?: boolean;
    readonly noRedirectFilter?: (genericId?: string) => void;
    readonly genericId?: string;
    readonly limit: number;
}

export interface MatchOfTheDayMatchListStoreProps {
    readonly sliceKey: string;
    readonly limit: number;
    readonly totalRecords: number;
    readonly ready: boolean;
    readonly locationId?: string;
    readonly eventCategoryId?: string;
    readonly preventRedirect?: boolean;
    readonly noRedirectFilter?: (genericId?: string) => void;
    readonly genericId?: string;
    readonly liveStatus?: DisciplineLiveStatus.LIVE | DisciplineLiveStatus.NOT_LIVE;
}
interface MatchOfTheDayMatchListDispatchProps {
    readonly addMore: (instanceId: string, limitNumberOfEvents: number) => void;
}

type MatchOfTheDayMatchListProps = MatchOfTheDayMatchListPublicProps
    & MatchOfTheDayMatchListStoreProps
    & MatchOfTheDayMatchListDispatchProps
    & I18nContextInterface
    & EMRoutingContextInterface;
const MatchOfTheDayMatchListComponent: React.FC<MatchOfTheDayMatchListProps> = ({
    instanceId,
    sliceKey,
    disciplineId,
    limit,
    limitNumberOfEvents,
    totalRecords,
    addMore,
    redirectAnchor,
    marketsHeaderOptions,
    showMixedMatchListHeader,
    itemLayout,
    mainMarketsLimit,
    selectGroupsOverview,
    groupOverview,
    filteredMarketGroupsOverviewList,
    filteredMarketGroupsOverview,
    isHasDrawer,
    deviceType,
    locationId,
    eventCategoryId,
    preventRedirect,
    noRedirectFilter,
    genericId,
    liveStatus,
}) => {

    return (
        <MatchList
            itemLayout={itemLayout}
            addMore={redirectAnchor ? undefined : addMore}
            addMoreAnchor={redirectAnchor}
            disciplineId={disciplineId}
            instanceId={instanceId}
            limit={limit}
            limitNumberOfEvents={limitNumberOfEvents}
            liveStatus={liveStatus}
            marketsHeaderOptions={marketsHeaderOptions}
            showMixedMatchListHeader={showMixedMatchListHeader}
            sliceKey={sliceKey}
            totalRecords={totalRecords}
            mainMarketsLimit={mainMarketsLimit}
            selectGroupsOverview={selectGroupsOverview}
            filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewList}
            filteredMarketGroupsOverview={filteredMarketGroupsOverview}
            isHasDrawer={isHasDrawer}
            deviceType={deviceType}
            groupOverview={groupOverview}
            locationId={locationId}
            eventCategoryId={eventCategoryId}
            preventRedirect={preventRedirect}
            noRedirectFilter={noRedirectFilter}
            genericId={genericId}
            sportId={disciplineId}
        />
    );
};

const MatchOfTheDayMatchList: React.ComponentType<MatchOfTheDayMatchListPublicProps> = compose<React.ComponentType<MatchOfTheDayMatchListPublicProps>>(
    withRouting(),
    withTranslation<MatchOfTheDayMatchListPublicProps>(),
    preLoader<MatchOfTheDayMatchListPublicProps>({
        preload({ props, dispatch }) {
            const { instanceId, disciplineId, limitNumberOfEvents, groupOverview, hasMarketSelector, locationId, eventCategoryId, liveStatus, dateRange } = props;
            const limit: number = limitNumberOfEvents || DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;
            if (hasMarketSelector && groupOverview) {
                dispatch(getMatchOfTheDayMatchGroupsOverviewList(instanceId, disciplineId, limit, groupOverview, locationId, eventCategoryId, liveStatus, dateRange));
            }
        },
        getReadySlice({ instanceId }: MatchOfTheDayMatchListPublicProps): string {
            const slice: string = getMatchOfTheDayMatchListSliceName(instanceId);

            return `${slice}.ready`;
        },
        update({ props, prevProps, dispatch }): void {
            const { instanceId, disciplineId, limitNumberOfEvents, groupOverview, locationId, eventCategoryId, liveStatus, dateRange } = props;
            const limit: number = limitNumberOfEvents || DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;
            if (groupOverview !== prevProps.groupOverview && disciplineId === prevProps.disciplineId) {
                if (groupOverview && prevProps.groupOverview && difference(groupOverview.split(','), prevProps.groupOverview.split(',')).length !== 0) {
                    dispatch(removeMatchOfTheDayMatchGroupsOverviewList(prevProps.instanceId, true));
                }
                dispatch(getMatchOfTheDayMatchGroupsOverviewList(instanceId, disciplineId, limit, groupOverview, locationId, eventCategoryId, liveStatus, dateRange));
            } else if (!areMatchOfTheDayMatchListTopicPropsEqual(prevProps, props)) {
                dispatch(updateMatchOfTheDayMatchGroupsOverviewList(instanceId, disciplineId, limit, groupOverview!, locationId, eventCategoryId, liveStatus, dateRange));
            }
        },
        destroy({ props, dispatch }) {
            const { instanceId } = props;
            dispatch(removeMatchOfTheDayMatchGroupsOverviewList(instanceId));
        },
        loadingAnimation: MatchListSkeleton,
    }),
    connect<MatchOfTheDayMatchListStoreProps,
        MatchOfTheDayMatchListDispatchProps,
        MatchOfTheDayMatchListPublicProps>(
            (state: AppState, props: MatchOfTheDayMatchListPublicProps): MatchOfTheDayMatchListStoreProps => {
                const { instanceId, preventRedirect, noRedirectFilter, genericId, liveStatus } = props;
                const sliceKey: string = getMatchOfTheDayMatchListSliceName(instanceId);

                const slice: MatchOfTheDayMatchListStoreModel | undefined = state[sliceKey];

                if (!slice) {

                    return {
                        sliceKey,
                        limit: 0,
                        totalRecords: 0,
                        ready: false,
                    };
                }

                return {
                    sliceKey,
                    limit: slice.limit,
                    totalRecords: slice.totalRecords,
                    ready: slice.ready,
                    preventRedirect,
                    noRedirectFilter,
                    genericId,
                    liveStatus,
                };
            },
            {
                addMore: addMoreMatchOfTheDayMatchList,
            },
        ),
)(MatchOfTheDayMatchListComponent);
export default MatchOfTheDayMatchList;
